<template>
  <div>
    <UserActivity :show="showActivity" :org="org" :orgList="orgList" :user="userToEdit" @close="showActivity = false;" />
    <UserPermissions v-if="showPermissions" :show="true" :org="org" :user="userToEdit" :users="usersToEdit" @close="showPermissions = false;" />

    <div class="search-container">
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        hide-details
      ></v-text-field>
    </div>
    <TableRefresh 
      :state="loading"
      @click.native="getUsers"
    />
    <v-data-table 
      v-model="selectedUsers"
      :headers="fiteredTableHeaders" 
      :items="users" 
      :loading="loading" 
      :search="search"
      show-select
      class="elevation-1 admin-table">
      <template v-slot:progress>
        <v-progress-linear color="blue" height="2" indeterminate></v-progress-linear>
      </template>
      <template v-slot:item.Role.name="{ item }">
         {{ mapUserRole(item.Role.name) }}
      </template>
      <template v-slot:item.hidden="{ item }">
        <v-switch v-model="item.hidden" color="primary" hide-details :disabled="loading" @change="toggleHidden(item)"></v-switch>
      </template>
      <template v-slot:item.disabled="{ item }">
        <v-switch v-model="item.status" color="primary" hide-details @change="toggleDeletePrompt(item)"></v-switch>
      </template>
      <template v-slot:item.action="{ item }">
        <div class="reports-action-launch" @click="launchRowAction(item, $event)">
        <v-icon>mdi-dots-vertical</v-icon>

        <ActionContainer :ref="item.key" :heading="'Action'" class="report-action-options">
          <div v-if="loadingActionMenu" class="d-flex justify-center align-center pt-3 pb-3 w-100">
            <v-progress-circular indeterminate size="20" width="2" />
          </div>

          <template v-else>
            <ActionItem
              :disabled="!$can('updateUserProfile')"
              label="Edit User Profile"
              icon="mdi-pencil"
              @click.native="editProfile(item)"
            />
            <ActionItem
              label="Activity"
              :disabled="!$can('createUserActivityReport')"
              icon="mdi-chart-line-variant"
              @click.native="showUserActivity(item)"
            />
            <ActionItem
              :disabled="!canEditPermissions(item)"
              label="Permissions"
              icon="mdi-lock"
              @click.native="showUserPermissions(item)"
            />
            <ActionItem
              v-if="canEditRole(item)"
              label="Edit User Role"
              icon="mdi-pencil"
              @click.native="editRole(item)"
            />
            <ActionItem
              v-if="canDeleteUser(item)"
              label="Edit Org Access"
              icon="mdi-key"
              :disabled="disableRestrictOrgAccess"
              @click.native="editOrgAccessToggle(item)"
            />
            <ActionItem
              v-if="canDeleteUser(item)"
              label="Delete User"
              icon="mdi-delete"
              @click.native="deleteUser(item)"
            />
            <ActionItem
              v-if="isAPIUser(item.Role?.name)"
              label="View API Key"
              icon="mdi-key"
              @click.native="toggleAPIKey(item)"
            />
            <ActionItem
              v-if="orgAPIState && isAPIUser(item.Role?.name)"
              label="Disable API Key"
              icon="mdi-lock"
              @click.native="setUserForAPIDisable(item)"
            />
            <ActionItem
              v-if="!orgAPIState && isAPIUser(item.Role?.name)"
              label="Enable API Key"
              icon="mdi-key"
              @click.native="setUserForAPIEnable(item)"
            />
          </template>
        </ActionContainer>
      </div>
      </template>
    </v-data-table>
    <v-dialog v-model="dialog" max-width="350px">
      <v-card>
        <v-card-title>
          <span class="headline">Update User Profile</span>
        </v-card-title>

        <v-card-text>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex md12>
                <v-text-field v-model="editedItem.firstName" label="First Name"></v-text-field>
              </v-flex>
              <v-flex md12>
                <v-text-field v-model="editedItem.lastName" label="Last Name"></v-text-field>
              </v-flex>
              <v-flex md12>
                <v-text-field v-model="editedItem.photo" label="Photo"></v-text-field>
              </v-flex>
              <v-flex md12>
                <v-text-field v-model="editedItem.telephone" label="Telephone"></v-text-field>
              </v-flex>
              <v-flex md12>
                <v-text-field v-model="editedItem.mobile" label="Mobile"></v-text-field>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="close">Cancel</v-btn>
          <v-btn v-if="!updatingProfile" color="blue darken-1" dark @click="save">Save</v-btn>
          <v-progress-circular
            v-else
            indeterminate
            color="primary"
          ></v-progress-circular>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="displayRoleEdit" width="500">
      <v-card>
          <v-card-title>
            <span class="headline">Update User Role</span>
          </v-card-title>

          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex md12>
                <v-text-field v-model="currentTargetRole" label="Current Role"></v-text-field>
              </v-flex>
              <v-flex md12>
                <v-select 
                  v-model="targetNewRole" 
                  label='Please select new role' 
                  :items="roleHierarchy[currentTargetRole]['authorised']"
                />
              </v-flex>
            </v-layout>

            <v-row>
              <v-col cols="12">
                <span v-if="roleAssignForbidden && targetNewRole != ''" class="forbidden-action">This action is not permitted</span>
              </v-col>
            </v-row>
            <v-card-actions>
              <v-btn color="red" text @click="closeEditRole">cancel</v-btn>
              <v-spacer></v-spacer>
              <v-btn color="green" text :disabled="roleAssignForbidden" :loading="updatingRole" @click="updateUserRole">Update</v-btn>
            </v-card-actions>
          </v-container>
      </v-card>
    </v-dialog>

    <Prompt
			:display="toggleEnableAPI"
			:close-target="'closeEnable'"
			:submit-target="'submitEnableKey'"
			:title="'Enable API Key'"
			:message="`Are you sure you wish to enable an API key for this user?`"
			:state="enableAPIState" 
			:res="enableAPIRes"
			@closeEnable="toggleEnableAPI = false"
			@submitEnableKey="enableAPIKey"
		/>

    <Prompt
			:display="toggleDisableAPI"
			:close-target="'closeDisable'"
			:submit-target="'submitDisableKey'"
			:title="'Disable API Key'"
			:message="`Are you sure you wish to disable this API key?`"
			:state="disableAPIState" 
			:res="disableAPIRes"
			@closeDisable="toggleDisableAPI = false"
			@submitDisableKey="disableAPIKey"
		/>

    <Prompt
			:display="disableUserConfirm"
			:close-target="'closeDelete'"
			:submit-target="'submitDeleteLog'"
			:title="`${!targetUserEnabled ? 'Disable' : 'Enable'} User`"
			:message="`Are you sure you wish to ${!targetUserEnabled ? 'disable' : 'enable'} this user?`"
			:state="enableUserState" 
			:res="enableUserRes"
			@closeDelete="toggleDeletePrompt(false)"
			@submitDeleteLog="changeUserStatus"
		/>

    <DeleteUser
      :display="toggleDeleteUser"
      :user="deleteTarget"
      @deleteUser="removeUserFromList"
      @close="toggleDeleteUser = false"
    />

    <OrgAccess 
      :users="eligibleUsersForOrgAccessUpdate"
      :display="displayOrgAccess"
      :orgs="orgs"
      @close="editOrgAccessToggle(null)"
    />

    <v-dialog 
      v-model="showAPIKey"
      width="500"
    >
      <v-card>
      <v-card-title class="text-h5 grey lighten-2">
        API Key
      </v-card-title>
      <br>
      <v-card-text class="prompt-text">
        <div 
          class="copy-key" 
          @click="copyKey"
        >
          Copy key to clipboard <v-icon>mdi-content-copy</v-icon>
        </div>
        <br>
        <br>
        {{ orgAPI }}
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          text
          @click="toggleAPIKey(null)"
        >
          CLOSE
        </v-btn>
      </v-card-actions>
    </v-card>
    </v-dialog>
  </div>
</template>

<script>

import {
  ListOrgUsers, 
  UpdateUserInOrg, 
  UpdateUserStatusInOrg, 
  getOrgAPIKey,
  enableAPIKey,
  disableOrgAPIKey,
  ListAllRoles, 
  updateUserRole,
  ListAccessibleOrgs,
} from "@/services/API.js"

import { AmplifyEventBus } from "aws-amplify-vue";
import Auth from "@/services/Auth";

import UserActivity from "./UserActivity.vue";
import UserPermissions from "@/components/permissions/UserPermissions.vue"
import TableRefresh from '@/components/general/TableRefresh.vue';
import Prompt from '@/components/general/Prompt.vue';
import ActionContainer from '@/components/general/ActionContainer.vue';
import ActionItem from '@/components/general/ActionItem.vue';
import DeleteUser from '../components/DeleteUser.vue';
import OrgAccess from '@/components/general/OrgAccess.vue';

import { mapState } from "vuex";

import { Logger } from "aws-amplify"
const logger = new Logger("OrgUsersList")

export default {
  components: {
    TableRefresh,
    Prompt,
    ActionContainer,
    ActionItem,
    DeleteUser,
    UserActivity,
    UserPermissions,
    OrgAccess
  },
  props: {
    orgNodeList: {
      type: Array,
      default: () => []
    },
    orgList: {
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    search: '',
    headers: [
      { text: "Email", value: "email" },
      { text: "First Name", value: "Profile.firstname" },
      { text: "Last Name", value: "Profile.lastname" },
      { text: "Telephone", value: "Profile.telephone" },
      { text: "Mobile", value: "Profile.mobile" },
      { text: "User Role", value: "Role.name" },
      { text: "Created On", value: "Profile.created_on" },
      { text: "Hidden", value: "hidden" },
      { text: "Enabled", value: "disabled" },
      { text: "Actions", value: "action", sortable: false },
    ],
    users: [],
    loading: false,
    noresult: false,
    dialog: false,
    editedIndex: -1,
    editedItem: {
      firstName: "",
      lastName: "",
      photo: "",
      telephone: "",
      mobile: "",
    },
    defaultItem: {
      firstName: "",
      lastName: "",
      photo: "",
      telephone: "",
      mobile: "",
    },
    
    updatingProfile: false,
    userToEdit: false,
    usersToEdit: [], // for editing a set of users
    disableUserConfirm: false,
    enableUserState: false,
    enableUserRes: null,
    targetUser: {},
    showActivity: false,
    showPermissions: false,
    targetUserEnabled: false, // true if the user is enabled, false if they are disabled,
    toggleDeleteUser: false,
    deleteTarget: {},
    orgAPI: '',
    orgAPIState: false,
    toggleDisableAPI: false,
    disableAPIState: false,
    disableAPIRes: null,

    toggleEnableAPI: false,
    enableAPIState: false,
    enableAPIRes: null,
    targetEnableAPIUSer: '',

    showAPIKey: false,

    roles: [],
    displayRoleEdit: false,
    user: null,
    roleHierarchy: {
      "FLEET_MANAGER": {
        authorised: ["FLEET_MANAGER", "ORG_ADMIN", "GROUP_ADMIN"],
        parentRoles: ["FLEET_MANAGER","FIRMWARE_MANAGER", "SUPPORT_ADMIN","SYS_ADMIN","ORG_ADMIN"],
        childRoles: ["DRIVER"]
      },
      "GROUP_ADMIN": {
        authorised: ["FLEET_MANAGER", "ORG_ADMIN", "GROUP_ADMIN"],
        parentRoles: ["FLEET_MANAGER", "FIRMWARE_MANAGER", "SUPPORT_ADMIN","SYS_ADMIN","ORG_ADMIN"],
        childRoles: ["DRIVER", "FLEET_MANAGER"]
      },
      "THIRD_PARTY_USER": {
        authorised: [],
        parentRoles: ["FLEET_MANAGER","FIRMWARE_MANAGER", "SUPPORT_ADMIN","SYS_ADMIN","ORG_ADMIN"],
        childRoles: []
      },
      "SUPPORT_ADMIN": {
        authorised: ["FLEET_MANAGER", "SUPPORT_ADMIN", "SUPPORT_LEAD", "ORG_ADMIN", "SYS_ADMIN", "GROUP_ADMIN"],
        parentRoles: [],
        childRoles: ["FLEET_MANAGER","ORG_ADMIN","DRIVER", "GROUP_ADMIN"],
      },
      "SUPPORT_LEAD": {
        authorised: ["FLEET_MANAGER", "SUPPORT_ADMIN","ORG_ADMIN", "SYS_ADMIN", "GROUP_ADMIN"],
        parentRoles: [],
        childRoles: ["FLEET_MANAGER","ORG_ADMIN", "SUPPORT_ADMIN", "DRIVER", "GROUP_ADMIN"]
      },
      "SYS_ADMIN": {
        authorised: ["FLEET_MANAGER","SUPPORT_LEAD", "FIRMWARE_MANAGER", "SUPPORT_ADMIN","SYS_ADMIN","ORG_ADMIN", "GROUP_ADMIN"],
        parentRoles: [],
        childRoles: ["FLEET_MANAGER","FIRMWARE_MANAGER", "SUPPORT_ADMIN","ORG_ADMIN", "GROUP_ADMIN"],
      },
      "ORG_ADMIN": {
        authorised: ["FLEET_MANAGER","ORG_ADMIN", "GROUP_ADMIN"],
        parentRoles: ["FIRMWARE_MANAGER", "SUPPORT_ADMIN","SYS_ADMIN"],
        childRoles: ["FLEET_MANAGER","DRIVER", "GROUP_ADMIN"],
      },
      "FIRMWARE_MANAGER": {
        authorised: ["FLEET_MANAGER","FIRMWARE_MANAGER", "SUPPORT_ADMIN", "SUPPORT_LEAD", "ORG_ADMIN", "SYS_ADMIN", "GROUP_ADMIN"],
        parentRoles: [],
        childRoles: ["FLEET_MANAGER","ORG_ADMIN","DRIVER", "GROUP_ADMIN"],
      }
    },
    targetNewRole: '',
    updatingRole: false,
    
    selectedUsers: [],
    displayOrgAccess: false,
    orgs: [],

    currentTargetUser: null,

    targetDisableAPIUSer: '',
    loadingActionMenu: false
  }),
  computed: {
    ...mapState([
      'currentUser'
    ]),

    org() {
      if (this.$route.params.name) {
        return this.$route.params.name
      }
      return null
    },
    fiteredTableHeaders () {
				return this.headers.filter(i => !this.hideHeader[i.value])
			},
			hideHeader () {
				return {
					hidden: !this.$can('updateUserStatus'),
					disabled: !this.$can('deleteUser'),
				}
			},
    currentTargetRole() {
      if (!this.targetUser.Role) return 'SUPPORT_ADMIN';
      return this.targetUser.Role ? this.targetUser.Role.name : '';
    },
    userRole() {
      if (!this.user) return 'SUPPORT_ADMIN';
      return this.user.User ? this.user.User['custom:role'] : '';
    },
    userEmail () {
      return this.user && this.user.User
        ? this.user.User.email
        : false
    },
    roleAssignForbidden() {
      // List of roles that the current logged in user has access to, the roles below them and roles above them
      const authorised = this.roleHierarchy[this.userRole]['authorised'];
      const childRoles = this.roleHierarchy[this.userRole]['childRoles'];
      const parentRoles = this.roleHierarchy[this.userRole]['parentRoles'];
      const userEmail = this.userEmail

      if (this.targetNewRole == '') return true;
      
      if (!this.userEmail || this.targetUser.email == userEmail) return true;

      if (this.userRole != this.currentTargetRole) {
        if(authorised.includes(this.targetNewRole) && !parentRoles.includes(this.targetNewRole)) {
          return false;
        }
        else {
          return true;
        }
      }
      else if (this.userRole == this.currentTargetRole) {
        if(authorised.includes(this.targetNewRole) && !childRoles.includes(this.targetNewRole) && this.userRole == 'SUPPORT_ADMIN' || this.userRole == 'SYS_ADMIN') {
          return false;
        }
        else {
          return true;
        }
      }
      return true
    },
    currentTargetUserRole() {
      return this.currentTargetUser ? this.currentTargetUser.Role.name : null
    },
    disableRestrictOrgAccess() {
      if(!this.currentTargetUserRole) return false
      if(this.roleValues[this.userRole] < 5 || this.currentTargetUserRole == 'DRIVER' || this.currentTargetUserRole == 'FLEET_MANAGER' || this.currentTargetUserRole == 'ORG_ADMIN') {
        return true
      } else {
        return this.roleValues[this.currentTargetUserRole] >= this.roleValues[this.userRole] ? true : false
      }
      
    },
    eligibleUsersForOrgAccessUpdate() {
      return this.selectedUsers.filter(user => {
        return this.roleValues[user.Role.name] < this.roleValues[this.userRole]
      })
    },
    // a map of labels for the user roles
    roleMap () {
      return {
        "SYS_ADMIN": "Admin",
        "FLEET_MANAGER": 'Fleet Manager',
        "ORG_ADMIN": "Org Admin",
        "SUPPORT_ADMIN": "Support Admin",
        "FIRMWARE_MANAGER": "Firmware Manager",
        "GROUP_ADMIN": "Group Admin",
        "unknown": "-"
      }
    },
    roleValues () {
      // the heirarchy of roles
      return {
        "SYS_ADMIN": 6,
        "SUPPORT_LEAD": 5,
        "FIRMWARE_MANAGER": 5,
        "SUPPORT_ADMIN": 4,
        "ORG_ADMIN": 3,
        "GROUP_ADMIN": 2,
        "FLEET_MANAGER": 1,
        "THIRD_PARTY_USER": 0,
        "DRIVER": 0,
        "GEOTAB_USER": 0
      }
    },
    // we use this for exceptions to regular role values. the role in the key can not make changes to the role in the array
    // so FIRMWARE MANAGER can not make changes to ['SUPPORT_ADMIN'] for example
    roleExclusions () {
      return {
        FIRMWARE_MANAGER: ['SUPPORT_ADMIN']
      }
    }
  },
  watch: {
    dialog(val) {
      val || this.close()
    },
  },
  async created() {
    this.getUser();
    this.getUsers();
    this.getRoles();
    this.getOrgs();
  },
  methods: {
    async getOrgs() {
      this.orgs = [];
      
      const orgs = await ListAccessibleOrgs()
      this.orgs = orgs ? orgs.sort((a, b) => a.name.localeCompare(b.name)) : []
    },
    editOrgAccessToggle(user) {

      if(user && !this.selectedUsers.includes(user)) {
        this.selectedUsers.unshift(user)
      }

      if(user == null) {
        this.selectedUsers = []
      }

      this.displayOrgAccess = !this.displayOrgAccess
    },
    // returns a numerical value for a role, higher numbers are higher permision roles
    getRoleValue (role) {
      const roleValues = this.roleValues

      return roleValues.hasOwnProperty(role)
        ? roleValues[role]
        : 0
    },

    // logic for users being able to edit the permissions of another user
    canDeleteUser (item) {
       try {
        // if the user does not have the permission
        if(!this.$can('deleteUser')) {
          return false
        }

        // if the user is trying to edit themselves
        if(item.id == this.currentUserID) {
          return false
        }

        // if the users role is not higher than the user they are trying to edit
        if(this.getRoleValue(this.userRole) <= this.getRoleValue(item.Role.name)) {
          return false
        }

        return true
      } catch (err) {
        logger.debug("error checking if user can set permissions", err, item, this.user)
        return false
      }
    },

    // logic for users being able to edit the permissions of another user
    canEditPermissions (item) {
       try {
        // if the user does not have the permission
        if(!this.$can('updateUserPermissions')) {
          return false
        }

        // if the user is trying to edit themselves
        if(item.id == this.currentUserID) {
          return false
        }

        // see this.roleExclusions above. we use this to enforce any non-standard rules
        if(this.roleExclusions[this.userRole] && this.roleExclusions[this.userRole].includes(item.Role.name)) {
          return false
        }

        // if the users role is not higher than the user they are trying to edit
        if(this.getRoleValue(this.userRole) <= this.getRoleValue(item.Role.name)) {
          return false
        }

        return true
      } catch (err) {
        logger.debug("error checking if user can set permissions", err, item, this.user)
        return false
      }
    },

    // logic for role editing
    canEditRole (item) {
      try {
        // if the user does not have the permission
        if(!this.$can('updateUserRole')) {
          return false
        }

        // if the user is trying to edit themselves
        if(item.id == this.currentUserID) {
          return false
        }

        // see this.roleExclusions above. we use this to enforce any non-standard rules
        if(this.roleExclusions[this.userRole] && this.roleExclusions[this.userRole].includes(item.Role.name)) {
          return false
        }

        // if the users role is not higher than the user they are trying to edit
        if(this.getRoleValue(this.userRole) <= this.getRoleValue(item.Role.name)) {
          return false
        }

        return true
      } catch (err) {
        logger.debug("error checking if user can set permissions", err, item, this.user)
        return false
      }
    },

    copyKey() {
      navigator.clipboard.writeText(this.orgAPI);
      AmplifyEventBus.$emit("snackEvent", {color: "green", text: "API key copied to clipboard"})
    },

    async toggleAPIKey(user) {
      if(user) {
       await this.getAPIKey(user.id)
      }
      this.showAPIKey = !this.showAPIKey;
    },

    displayDisableAPI() {
      this.disableAPIRes = null;
      this.toggleDisableAPI = true;
    },

    showUserPermissions (user) {
      logger.debug("user to edit", user)
      let selectedUsers = this.selectedUsers
      // reset our state first
      if(selectedUsers.length) {
        if(!selectedUsers.find(i => i.id == user.id)) {
          selectedUsers.push(user)
        }
      }

      this.userToEdit = user
      this.usersToEdit = selectedUsers
      this.showPermissions = true
    },

    setUserForAPIDisable(user) {
      this.toggleDisableAPI = !this.toggleDisableAPI

      const id = user.id

      this.targetDisableAPIUSer = id
    },
    

    setUserForAPIEnable(user) {
      logger.info('setUserForAPIEnable ', user)
      const id = user.id

      this.targetEnableAPIUSer = id
      this.toggleEnableAPI = true;
      this.enableAPIRes = null
    },

    async enableAPIKey() {
      this.enableAPIState = !this.enableAPIState
      const res = await enableAPIKey(this.org, this.targetEnableAPIUSer)

      if (res) {
        this.enableAPIState = !this.enableAPIState
        this.enableAPIRes = {
          msg: `API access enabled`,
          code: 'green'
        }
      } else {
        this.enableAPIState = !this.enableAPIState
        this.enableAPIRes = {
          msg: `An error occurred`,
          code: 'red'
        }
      }
    },

    async disableAPIKey() {
      this.disableAPIState = !this.disableAPIState
      this.disableAPIRes = null
     
      const res = await disableOrgAPIKey(this.org, this.targetDisableAPIUSer)

      if (res) {
        this.disableAPIState = !this.disableAPIState
        this.disableAPIRes = {
          msg: `API access has been disabled`,
          code: 'green'
        }
      } else {
        this.disableAPIState = !this.disableAPIState
        this.disableAPIRes = {
          msg: `An error occurred`,
          code: 'red'
        }
      }
    },

    isAPIUser(role) {
      return role == 'THIRD_PARTY_USER' || role == 'API_USER' ? true : false;
    },

    mapUserRole(role) {
      const map = {
        THIRD_PARTY_USER: 'API_USER'
      };

      return Object.hasOwn(map, role) ? map[role] : role;
    },
    
    removeUserFromList(id) {
      this.users = this.users.filter(user => user.id != id);
    },

    deleteUser(user) {
      this.deleteTarget = user;
      this.toggleDeleteUser = !this.toggleDeleteUser;
    },

    showUserActivity (user) {
      this.userToEdit = user
      this.showActivity = true
    },

    async updateUserRole() {
      this.updatingRole = true;
      const userEmail = this.targetUser.email;
      const userID = this.targetUser.id;
      const res = await updateUserRole(this.org, userEmail, this.targetNewRole);

      if (res.role) {
        let orgUserIndex = this.users.findIndex(user => user.id == userID);

        this.users[orgUserIndex].Role.name = res.role;
        this.updatingRole = false;
        this.displayRoleEdit = false;
        this.displayRoleEdit = false;
        AmplifyEventBus.$emit("snackEvent", {color: "green", text: `${userEmail} role changed to ${res.role}`});
      } else {
        
        this.updatingRole = false;
        AmplifyEventBus.$emit("snackEvent", {color: "red", text: res});
      }
    },

    closeEditRole() {
      this.targetUser = {};
      this.displayRoleEdit = !this.displayRoleEdit;
    },

    editRole(user) {
      this.targetUser = user;
      this.displayRoleEdit = !this.displayRoleEdit;
      this.targetNewRole = '';
    },

    async getRoles() {
      const res = await ListAllRoles()
      if (res) this.roles = res.Roles
    },

    async getUser() {
      const user = await Auth.currentSession();

      this.user = user;
    },

    async launchRowAction(user, e) {
      this.currentTargetUser = user

      const allOptions = document.querySelectorAll(".report-action-options")
      const target = e?.target?.nextSibling
      const opening = !!target && !target.classList.contains('active-options')

      // close every open menu, then reopen this one immediately if we're opening it
      allOptions.forEach(option => option.classList.remove('active-options'))
      if (target && opening) {
        target.classList.add('active-options')
      }

      // only fetch the API key (and only show loading state) when actually opening
      if (opening) {
        const role = user?.Role?.name
        if (role === 'THIRD_PARTY_USER' || role === 'API_USER') {
          this.loadingActionMenu = true
          try {
            await this.getAPIKey(user.id)
          } catch (err) {
            logger.info('getAPIKey failed', err)
          } finally {
            this.loadingActionMenu = false
          }
        }
      }
    },

    toggleDeletePrompt(item) {
      // when the dialog box is opened, our default behavior should be to reset the users disabled status in the table, unless an update is actually processed
      if(!this.disableUserConfirm) {
        this.resetUserStatus = true
      }

      // if the dialog is currently open, and we should reset the user status because no update has been made
      // if the user closes the diaog, we need to undo the change to the users status in the table
      if(this.resetUserStatus && this.disableUserConfirm ) {
        this.users.map(i => {
          if(i.id == this.targetUser.id) {
            i.status = !i.status
          }
          return i
        })
      }

      this.enableUserState = false;
      this.targetUser = item; 
      this.targetUserEnabled = item ? item.status : false;
      this.enableUserRes = null
      this.disableUserConfirm = !this.disableUserConfirm;
    },
    toggleHidden(item) {
      this.loading = true
      logger.debug("set hidden", item.hidden)

      UpdateUserInOrg(item.Org.id, item.email, { hidden: item.hidden }).then(res => {
        logger.debug("hidden status update", res)
        this.loading = false
      }).catch(err => {
        item.hidden = !item.hidden
        logger.debug("hidden status err", err)
        this.loading = false
      })
    },
    async getAPIKey(userId) {
      const apiInfo = await getOrgAPIKey(this.org, userId)

      if (apiInfo) {
        this.orgAPI = apiInfo.api_key
        this.orgAPIState = apiInfo.enabled
      }
    },

    async getUsers() {
      if (this.$route.params.name) {
        // const name = this.$route.params.name.replace(/-/g, " ");
        this.loading = true;
        this.selectedUsers = [];

        const res = await ListOrgUsers(this.org, true)
        
        if (res && res.Users.length > 0) {
          this.loading = false
          logger.debug("users", res)
          this.users = res.Users.map((user) => {
            user.status = !user.disabled // we want the switch to be off if the user is disabled
            return user
          })
          this.$emit('usersFetched', this.users);
        } else {
          this.loading = false
          this.noresult = true
        }
      }
    },
    editProfile(item) {
      logger.info('item to edit ', item)
      this.editedIndex = this.users.indexOf(item)
      this.editedItem = Object.assign(
        {},
        {
          firstName: item.Profile.firstname,
          lastName: item.Profile.lastname,
          photo: item.Profile.photo,
          telephone: item.Profile.telephone,
          mobile: item.Profile.mobile,
        }
      )
      this.dialog = true
    },

    deleteItem(item) {
      const index = this.users.indexOf(item)
      confirm("Are you sure you want to delete this item?") && this.users.splice(index, 1)
    },

    async changeUserStatus() {
      const res = await UpdateUserStatusInOrg(this.targetUser.Org.id, this.targetUser.email, this.targetUser.status)

      if (res) {
        this.enableUserState = true;
        this.enableUserRes = {
          msg: `User has been ${this.targetUser.status ? "enabled" : "disabled"}`,
          code: 'green'
        };
        this.resetUserStatus = false;
      } else {
        const index = this.users.indexOf(this.targetUser)
        this.users[index].status = !this.users[index].status

        this.enableUserState = true;
        this.enableUserRes = {
          msg: `An error occurred`,
          code: 'red'
        };
        this.resetUserStatus = true
      }
    },

    close() {
      this.dialog = false
      this.editedItem = Object.assign({}, this.defaultItem)
      this.editedIndex = -1
    },

    async save() {
      if (this.editedIndex > -1 && this.$route.params.name) {
        this.updatingProfile = !this.updatingProfile

        const user = this.users[this.editedIndex]
        logger.info('edit user ', user)
        if(!this.editedItem.firstName.length || !this.editedItem.lastName.length) {
          AmplifyEventBus.$emit("snackEvent", {color: "red", text: "First name and Last name are required!"})
          return
        }

        // const name = this.$route.params.name.replace(/-/g, " ");
        const res = await UpdateUserInOrg(this.org, user.email, this.editedItem)
        if (res) {
          const editedUser = { 
            ...this.users[this.editedIndex], 
            Profile: {
              ...{ 
                firstname: this.editedItem.firstName,
                lastname: this.editedItem.lastName,
                photo: this.editedItem.photo,
                telephone: this.editedItem.telephone,
                mobile: this.editedItem.mobile, 
                created_on: user.Profile.created_on
              } 
              }
            }
          this.users.splice(this.editedIndex, 1, editedUser)
          this.close()
        }
        this.updatingProfile = !this.updatingProfile
      }
    },
  },
}
</script>

<style lang="scss">
  .search-container {
    width: 20em;
    padding-left: 1em;
  }
	.reports-action-launch {
    position: relative;
    cursor: pointer;
  }
	.report-action-options {
    display: none;
  }
	.active-options {
    display: block !important;  
  }
  .forbidden-action {
    color: #F44336;
    text-align: center;
    display: block;
  }
	#admin-vehicles-table {
		.v-data-table__wrapper {
			overflow-x: visible !important;
			overflow-y: visible !important;
		}
	}

  .v-data-table__wrapper {
    overflow-x: visible !important;
    overflow-y: visible !important;
  }
  .copy-key {
    position: relative;
    z-index: 2;
    cursor: pointer;
  }
</style>
